'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useId, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { WarningGlyph } from '../../../internal/icons.js';
import { Button } from '../../actions/button/index.js';
import { Alert } from '../../feedback/alert/index.js';
import { Field, FieldDescription, FieldError, FieldLabel } from '../../forms/form-field/index.js';
import { Input, Textarea } from '../../forms/text-field/index.js';
import { Dialog, DialogBody, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, } from '../dialog/index.js';
const DEFAULT_ERROR = 'That did not work. Try again.';
function defaultErrorMessage(error) {
    return error instanceof Error && error.message ? error.message : DEFAULT_ERROR;
}
function isThenable(value) {
    return (typeof value === 'object' &&
        value !== null &&
        typeof value.then === 'function');
}
/**
 * Confirmation for consequential actions, built on Dialog with
 * `role="alertdialog"`. Supports a danger tone, a consequence list,
 * type-to-confirm, an optional or required reason, and async actions with
 * pending and error states. Focus starts on Cancel, the least destructive
 * action, and nothing dismisses the dialog while the action runs.
 */
export const ConfirmDialog = forwardRef(function ConfirmDialog({ open: openProp, defaultOpen = false, onOpenChange, trigger, title, description, consequences, children, tone = 'default', confirmLabel = 'Confirm', cancelLabel = 'Cancel', confirmPhrase, confirmPhraseLabel, confirmPhraseMismatch = 'The text does not match.', reason, onConfirm, onCancel, pending: pendingProp, error: errorProp, errorMessage = defaultErrorMessage, closeOnConfirm = true, initialFocus = 'cancel', size = 'sm', closeOnOutsideClick = false, closeLabel = null, onOpenAutoFocus, className, ...rest }, ref) {
    const [openState, setOpenState] = useState(defaultOpen);
    const open = openProp ?? openState;
    const [phrase, setPhrase] = useState('');
    const [reasonText, setReasonText] = useState('');
    const [running, setRunning] = useState(false);
    const [failure, setFailure] = useState(null);
    const [attempted, setAttempted] = useState(false);
    const cancelRef = useRef(null);
    const fieldRef = useRef(null);
    const mounted = useRef(true);
    const errorId = useId();
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    // Every opening starts clean: no typed phrase, reason or old error.
    useEffect(() => {
        if (open) {
            setPhrase('');
            setReasonText('');
            setFailure(null);
            setAttempted(false);
        }
    }, [open]);
    const pending = pendingProp ?? running;
    const shownError = errorProp ?? failure;
    const phraseOk = confirmPhrase === undefined || phrase.trim() === confirmPhrase.trim();
    const reasonOk = !reason?.required || reasonText.trim() !== '';
    const blocked = !phraseOk || !reasonOk;
    const setOpen = useCallback((next) => {
        if (openProp === undefined)
            setOpenState(next);
        onOpenChange?.(next);
    }, [openProp, onOpenChange]);
    const handleOpenChange = (next) => {
        if (!next && pending)
            return;
        if (!next)
            onCancel?.();
        setOpen(next);
    };
    const submit = async (event) => {
        event?.preventDefault();
        if (pending)
            return;
        if (blocked) {
            setAttempted(true);
            return;
        }
        setFailure(null);
        const trimmed = reasonText.trim();
        const details = reason && trimmed !== '' ? { reason: trimmed } : {};
        try {
            const result = onConfirm(details);
            if (isThenable(result)) {
                setRunning(true);
                await result;
            }
        }
        catch (error) {
            if (mounted.current) {
                setRunning(false);
                setFailure(errorMessage(error));
            }
            return;
        }
        if (!mounted.current)
            return;
        setRunning(false);
        if (closeOnConfirm)
            setOpen(false);
    };
    const hasField = confirmPhrase !== undefined || reason !== undefined;
    const hasBody = children !== undefined || (consequences && consequences.length > 0) || hasField;
    return (_jsxs(Dialog, { open: open, onOpenChange: handleOpenChange, children: [trigger ? _jsx(DialogTrigger, { asChild: true, children: trigger }) : null, _jsx(DialogContent, { ref: ref, role: "alertdialog", ...rest, size: size, busy: pending, closeLabel: closeLabel, closeOnOutsideClick: closeOnOutsideClick, "data-tone": tone, className: cx('mn-confirm-dialog', className), onOpenAutoFocus: (event) => {
                    onOpenAutoFocus?.(event);
                    if (event.defaultPrevented)
                        return;
                    event.preventDefault();
                    const target = initialFocus === 'field' && fieldRef.current ? fieldRef.current : null;
                    (target ?? cancelRef.current)?.focus();
                }, children: _jsxs("form", { className: "mn-confirm-dialog__form", onSubmit: submit, noValidate: true, children: [_jsxs(DialogHeader, { className: "mn-confirm-dialog__header", children: [tone === 'danger' ? (_jsx("span", { className: "mn-confirm-dialog__icon", "aria-hidden": true, children: _jsx(WarningGlyph, {}) })) : null, _jsx(DialogTitle, { children: title }), description ? _jsx(DialogDescription, { children: description }) : null] }), hasBody ? (_jsxs(DialogBody, { className: "mn-confirm-dialog__body", children: [children, consequences && consequences.length > 0 ? (_jsx("ul", { className: "mn-confirm-dialog__consequences", children: consequences.map((item, index) => (_jsx("li", { children: item }, index))) })) : null, reason ? (_jsxs(Field, { required: reason.required ?? false, invalid: attempted && !reasonOk, children: [_jsx(FieldLabel, { children: reason.label }), reason.description ? (_jsx(FieldDescription, { children: reason.description })) : null, _jsx(Textarea, { ref: confirmPhrase === undefined ? fieldRef : undefined, value: reasonText, onChange: (event) => {
                                                setReasonText(event.target.value);
                                                if (attempted)
                                                    setAttempted(false);
                                            }, placeholder: reason.placeholder, maxLength: reason.maxLength ?? 1000, readOnly: pending, rows: 3 }), _jsx(FieldError, { children: attempted && !reasonOk
                                                ? (reason.requiredMessage ?? 'Enter a reason to continue.')
                                                : null })] })) : null, confirmPhrase !== undefined ? (_jsxs(Field, { invalid: attempted && !phraseOk, children: [_jsx(FieldLabel, { children: confirmPhraseLabel ?? (_jsxs(_Fragment, { children: ["Type ", _jsx("span", { className: "mn-confirm-dialog__phrase", children: confirmPhrase }), " to confirm"] })) }), _jsx(Input, { ref: fieldRef, value: phrase, onChange: (event) => {
                                                setPhrase(event.target.value);
                                                if (attempted)
                                                    setAttempted(false);
                                            }, onKeyDown: (event) => {
                                                // A disabled submit button blocks implicit submission, so
                                                // handle Enter here to report a mismatch instead of ignoring it.
                                                if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                                                    event.preventDefault();
                                                    void submit();
                                                }
                                            }, readOnly: pending, autoComplete: "off", autoCapitalize: "off", autoCorrect: "off", spellCheck: false }), _jsx(FieldError, { children: attempted && !phraseOk ? confirmPhraseMismatch : null })] })) : null] })) : null, shownError ? (_jsx("div", { className: "mn-confirm-dialog__error", children: _jsx(Alert, { tone: "danger", live: "assertive", id: errorId, children: shownError }) })) : null, _jsxs(DialogFooter, { className: "mn-confirm-dialog__footer", children: [_jsx(DialogClose, { asChild: true, children: _jsx(Button, { ref: cancelRef, disabled: pending, children: cancelLabel }) }), _jsx(Button, { type: "submit", variant: tone === 'danger' ? 'danger' : 'primary', loading: pending, disabled: blocked, "aria-describedby": shownError ? errorId : undefined, children: confirmLabel })] })] }) })] }));
});
/**
 * Where focus should return when the dialog closes: the focused element, or,
 * when that is an item of a menu that is about to close, the menu's trigger.
 */
function focusReturnTarget() {
    if (typeof document === 'undefined')
        return null;
    const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const menuOwner = active?.closest('[role="menu"]')?.getAttribute('aria-labelledby');
    const trigger = menuOwner ? document.getElementById(menuOwner) : null;
    return trigger ?? active;
}
/**
 * Imperative confirmation: `if (await confirm({ title: 'Delete note?' }))`.
 * A replacement for `window.confirm` and `window.prompt` that is themed,
 * accessible and can show consequences, a reason field and async progress.
 */
export function useConfirm() {
    const [request, setRequest] = useState(null);
    const [open, setOpen] = useState(false);
    const resolver = useRef(null);
    const counter = useRef(0);
    const returnFocus = useRef(null);
    const settle = useCallback((value) => {
        resolver.current?.(value);
        resolver.current = null;
    }, []);
    useEffect(() => () => settle(null), [settle]);
    const confirm = useCallback((options) => {
        settle(null);
        returnFocus.current = focusReturnTarget();
        counter.current += 1;
        setRequest({ id: counter.current, options });
        setOpen(true);
        return new Promise((resolve) => {
            resolver.current = resolve;
        });
    }, [settle]);
    const options = request?.options;
    const element = (_jsx(ConfirmDialog, { title: "", ...options, open: open && request !== null, onCloseAutoFocus: (event) => {
            options?.onCloseAutoFocus?.(event);
            const target = returnFocus.current;
            if (event.defaultPrevented || !target?.isConnected)
                return;
            event.preventDefault();
            target.focus();
        }, onOpenChange: (next) => {
            setOpen(next);
            if (!next)
                settle(null);
        }, onConfirm: async (details) => {
            await options?.onConfirm?.(details);
            settle(details);
        } }, request?.id ?? 0));
    return { confirm, element };
}
//# sourceMappingURL=confirm-dialog.js.map